import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { TokenService } from './token.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

/**
 * Intercepta los HTTP requests para dirigirlos al API y agregar las credenciales
 * @author Juan Corral
 */
@Injectable()
export class InterceptorAPI implements HttpInterceptor {
  constructor(
    private _tokenService: TokenService,
    private _router: Router,
  ) {}

  private readonly BASE_URL: string = environment.apiUrl;

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Si el request es para una URL externa, mandar el request sin token
    if (request.url.indexOf('http') > -1) return next.handle(request);

    // Dirigir el request al URL del API
    let apiReq = request.clone({ url: `${this.BASE_URL}/${request.url}` });

    // Agregar slash final si no lo tiene, antes del query string (una URL escrita a mano
    // con el query inline quedaría si no como `.../ordenes?estado=activa/`)
    const inicioQuery = apiReq.url.indexOf('?');
    const ruta = inicioQuery === -1 ? apiReq.url : apiReq.url.slice(0, inicioQuery);
    const query = inicioQuery === -1 ? '' : apiReq.url.slice(inicioQuery);
    if (!ruta.endsWith('/')) apiReq = apiReq.clone({ url: `${ruta}/${query}` });

    // Si el request es para un endpoint de autenticación, mandar el request sin token
    if (
      request.url.indexOf('autenticacion') > -1 &&
      request.url.indexOf('logout') == -1 &&
      request.url.indexOf('usuario') == -1 &&
      request.url.indexOf('verificaciones') == -1
    )
      return next.handle(apiReq);

    // Agregar access token
    const access = this._tokenService.accessToken;
    if (access) {
      apiReq = apiReq.clone({
        headers: apiReq.headers.set('Authorization', `Bearer ${access}`),
      });
    }

    // Hace la petición
    return next.handle(apiReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Lanzar el error si no es 401
        if (error.status !== 401) return throwError(() => error);

        // Revisar si el refresh token ha expirado
        return from(this._tokenService.refreshTokenExpirado$()).pipe(
          switchMap((expirado: boolean) => {
            // Si el refresh token expiró, redirigir al login
            if (expirado) {
              this._tokenService.limpiarToken();
              this._router.navigate(['ingreso', 'login']);
              return throwError(() => error);
            }

            // Refrescar el token
            return from(this._tokenService.refrescarToken$()).pipe(
              switchMap((success) => {
                // Si no se pudo refrescar el token, redirigir al login
                if (!success) {
                  this._tokenService.limpiarToken();
                  this._router.navigate(['ingreso', 'login']);
                  return throwError(() => error);
                }

                // Construir petición con el nuevo access token
                const nuevoAccess = this._tokenService.accessToken;
                const retryReq = apiReq.clone({
                  headers: apiReq.headers.set('Authorization', `Bearer ${nuevoAccess}`),
                });

                // Reintentar
                return next.handle(retryReq);
              }),
            );
          }),
        );
      }),
    );
  }
}
